const CONTINUATION_KEY = 'paddiq:session-scan-continuation:v1';
const LOCATOR_PATTERN = /^[A-Za-z0-9_-]{43}$/;

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

type StoredContinuation = {
  version: 1;
  kind: 'session-scan';
  destination: string;
};

function hasExactKeys(value: Record<string, unknown>, keys: string[]) {
  const actual = Object.keys(value).sort();
  const expected = [...keys].sort();
  return actual.length === expected.length && actual.every((key, index) => key === expected[index]);
}

export function parseAuthReturnDestination(value: string) {
  const scanMatch = value.match(/^\/s\/v1\/([A-Za-z0-9_-]{43})$/);
  if (scanMatch && LOCATOR_PATTERN.test(scanMatch[1]!)) {
    return value;
  }

  if (
    !value.startsWith('/') ||
    value.startsWith('//') ||
    value.includes('\\') ||
    value.includes('#')
  ) {
    return null;
  }

  const queryIndex = value.indexOf('?');
  if (queryIndex < 0 || value.slice(0, queryIndex) !== '/sessions') {
    return null;
  }

  try {
    const parsed = new URL(value, 'https://session-scan.invalid');
    const keys = [...parsed.searchParams.keys()];
    const sessionId = parsed.searchParams.get('session');
    if (
      parsed.origin !== 'https://session-scan.invalid' ||
      parsed.pathname !== '/sessions' ||
      parsed.hash ||
      keys.length !== 1 ||
      keys[0] !== 'session' ||
      !sessionId ||
      !isSafeSessionId(sessionId)
    ) {
      return null;
    }

    const canonical = new URLSearchParams({ session: sessionId });
    return `/sessions?${canonical.toString()}`;
  } catch {
    return null;
  }
}

function isSafeSessionId(sessionId: string) {
  return (
    sessionId.length >= 1 &&
    sessionId.length <= 200 &&
    sessionId.trim() === sessionId &&
    !sessionId.includes('/')
  );
}

export function storeSessionScanContinuation(pathname: string, storage: StorageLike) {
  const destination = parseAuthReturnDestination(pathname);
  if (!destination?.startsWith('/s/v1/')) {
    storage.removeItem(CONTINUATION_KEY);
    return false;
  }

  const record: StoredContinuation = { version: 1, kind: 'session-scan', destination };
  storage.setItem(CONTINUATION_KEY, JSON.stringify(record));
  return true;
}

export function readSessionScanContinuation(storage: StorageLike) {
  const serialized = storage.getItem(CONTINUATION_KEY);
  if (!serialized) {
    return null;
  }

  try {
    const record = JSON.parse(serialized) as unknown;
    if (
      !record ||
      typeof record !== 'object' ||
      Array.isArray(record) ||
      !hasExactKeys(record as Record<string, unknown>, ['version', 'kind', 'destination'])
    ) {
      throw new Error('Invalid continuation shape');
    }

    const candidate = record as Partial<StoredContinuation>;
    if (
      candidate.version !== 1 ||
      candidate.kind !== 'session-scan' ||
      typeof candidate.destination !== 'string'
    ) {
      throw new Error('Invalid continuation values');
    }

    const destination = parseAuthReturnDestination(candidate.destination);
    if (!destination?.startsWith('/s/v1/')) {
      throw new Error('Invalid continuation destination');
    }

    return destination;
  } catch {
    storage.removeItem(CONTINUATION_KEY);
    return null;
  }
}

export function clearSessionScanContinuation(storage: StorageLike) {
  storage.removeItem(CONTINUATION_KEY);
}

export function buildExactSessionDestination(sessionId: string) {
  if (!isSafeSessionId(sessionId)) {
    return null;
  }
  return `/sessions?${new URLSearchParams({ session: sessionId }).toString()}`;
}
